// Tab "Thống kê": lọc nhanh khoảng ngày → thẻ số, hạng, ngân sách, Xu phát, giữ chân, phễu nhiệm vụ, kinh doanh, chi phí, bảng ngày.
// Hai API: /stats (theo ngày) và /stats/overview (tham gia, giữ chân, kinh doanh, chi phí). Overview lỗi vẫn hiện phần /stats.
import { useCallback, useEffect, useState } from "react";
import { Alert, Spin, message } from "antd";
import dayjs from "dayjs";
import xuProgramApi from "../../../api/xuProgramApi";
import XuStatsToolbar, { rangeOfPreset } from "./xu-stats-toolbar";
import XuStatsKpiRow from "./xu-stats-kpi-row";
import XuStatsRankAndBudget from "./xu-stats-rank-and-budget";
import XuStatsXuSection from "./xu-stats-xu-section";
import XuStatsRetentionSection from "./xu-stats-retention-section";
import XuStatsMissionsSection from "./xu-stats-missions-section";
import XuStatsBusinessSection from "./xu-stats-business-section";
import XuStatsCostSection from "./xu-stats-cost-section";
import XuStatsDailyTable from "./xu-stats-daily-table";
import { buildDailyCsv, downloadCsv } from "./xu-stats-csv";
import { apiError } from "./xu-format-helpers";

const FMT = "YYYY-MM-DD";

/** Tiêu đề mục: chữ đậm + mô tả 1 dòng cho người không rành kỹ thuật. */
const Section = ({ title, hint, children }) => (
    <section className="space-y-2">
        <div><h3 className="text-base font-semibold m-0">{title}</h3>{hint && <div className="text-xs text-gray-500">{hint}</div>}</div>
        {children}
    </section>
);

export default function XuStatsTab({ flags, onOpenUser }) {
    const [preset, setPreset] = useState("7");
    const [range, setRange] = useState(() => rangeOfPreset("7"));
    const [stats, setStats] = useState(null);
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const params = [range[0].format(FMT), range[1].format(FMT)];
        const [s, o] = await Promise.allSettled([xuProgramApi.getStats(...params), xuProgramApi.getStatsOverview(...params)]);
        if (s.status === "fulfilled") setStats(s.value.data.data);
        else message.error(apiError(s.reason, "Không tải được thống kê"));
        if (o.status === "fulfilled") setOverview(o.value.data.data);
        else {
            setOverview(null);
            message.error(apiError(o.reason, "Không tải được phần thống kê mở rộng"));
        }
        setLoading(false);
    }, [range]);
    useEffect(() => {
        load();
    }, [load]);

    const onPreset = (value) => {
        setPreset(value);
        if (value !== "custom") setRange(rangeOfPreset(value));
    };

    if (!stats) return <div className="py-16 text-center"><Spin /></div>;
    const empty = !stats.walletUsers && !stats.days.some((d) => d.earned || d.checkins);
    return (
        <div className="space-y-5">
            <XuStatsToolbar preset={preset} onPreset={onPreset} range={range} onRange={(v) => { setPreset("custom"); setRange(v); }}
                onRefresh={load} loading={loading} canExport={stats.days.length > 0}
                onExport={() => downloadCsv(`xu-theo-ngay-${stats.from}_${stats.to}.csv`, buildDailyCsv(stats.days, stats.missionTitles))} />
            {empty && (
                <Alert type="info" showIcon
                    message={flags && !flags.global ? "Chưa có ai tham gia — chương trình đang TẮT" : "Chưa có ai tham gia chương trình"}
                    description={flags && !flags.global ? "Bật công tắc tổng và thêm tài khoản thử ở tab Cấu hình. Khi có người điểm danh, các biểu đồ dưới đây sẽ tự có số." : "Số liệu sẽ xuất hiện khi có người điểm danh hoặc làm nhiệm vụ."} />
            )}
            <XuStatsKpiRow stats={stats} overview={overview} />
            <XuStatsRankAndBudget stats={stats} />
            <Section title="Xu phát ra" hint={`Từ ${dayjs(stats.from).format("DD/MM")} đến ${dayjs(stats.to).format("DD/MM/YYYY")}`}>
                <XuStatsXuSection days={stats.days} bySource={overview?.cost.earnBySource || []} />
            </Section>
            {overview && (
                <>
                    <Section title="Giữ chân người dùng" hint="Người dùng có quay lại điểm danh mỗi ngày không?">
                        <XuStatsRetentionSection overview={overview} />
                    </Section>
                    <Section title="Nhiệm vụ hằng ngày" hint="Bao nhiêu người làm xong, bao nhiêu người nhận Xu, bao nhiêu người làm đủ tất cả nhiệm vụ đang bật.">
                        <XuStatsMissionsSection days={stats.days} funnel={overview.funnel} missionTitles={stats.missionTitles} />
                    </Section>
                    <Section title="Hiệu quả kinh doanh" hint="Người tham gia chương trình Xu có mua nhiều hơn người chưa tham gia không?">
                        <XuStatsBusinessSection business={overview.business} />
                    </Section>
                    <Section title="Chi phí & nợ Xu" hint="LH đang nợ người dùng bao nhiêu Xu và ai nhận nhiều nhất.">
                        <XuStatsCostSection cost={overview.cost} onOpenUser={onOpenUser} />
                    </Section>
                </>
            )}
            <XuStatsDailyTable days={stats.days} loading={loading} missionTitles={stats.missionTitles} />
        </div>
    );
}
